import React from "react";
import { createPortal } from "react-dom";
import { useClickOutside } from "../../../../hooks/useClickOutside";
import { useFloatingCoords } from "../../../../hooks/useFloatingCoords";
import type { DepGraphPredType } from "../../../../models";
import type {
  DepCandidate,
  ExternalCandidate,
  RelPickConfig,
} from "../../../../models";
import { autoFontColor } from "../../../../utils/color";
import { formatDateMonthDay } from "../../../../utils/format";
import "./DepChipTypeahead.css";

export type {
  DepCandidate,
  ExternalCandidate,
  RelPickConfig,
} from "../../../../models";

/** Does a candidate match the search query? Matches across work code, the
 *  work-code name, the P6 task_name, AND the zone — so "11", "Complete OH MEP",
 *  "MEP in-wall", or "LVL3" all find the same activity. */
export function candidateMatches(c: DepCandidate, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    c.workCode.toLowerCase().includes(q) ||
    c.displayName.toLowerCase().includes(q) ||
    c.taskName.toLowerCase().includes(q) ||
    c.zoneLabel.toLowerCase().includes(q)
  );
}

const REL_TYPES: DepGraphPredType[] = ["FS", "SS", "FF", "SF"];

interface Props {
  candidates: DepCandidate[];   // caller-filtered (self excluded), sorted start asc
  externalCandidates?: ExternalCandidate[];
  /** Query-driven out-of-section search: when set, the popover fetches matching
   *  externals from the server as you type (≥2 chars) instead of filtering a
   *  static list. Results render under "Out of section". */
  fetchExternals?: (query: string) => Promise<ExternalCandidate[]>;
  onPick?: (candidate: DepCandidate) => void;
  onPickExternal?: (candidate: ExternalCandidate) => void;
  onClose: () => void;
  /** When set, the popover renders via a body portal at fixed viewport coords
   *  beneath this element. Use for nested-modal contexts where parent overflow
   *  or stacking would otherwise clip / push around the absolute popover. */
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** Combined search + TYPE + LAG mode. */
  withRel?: RelPickConfig;
}

const DepChipTypeahead: React.FC<Props> = ({
  candidates,
  externalCandidates = [],
  fetchExternals,
  onPick,
  onPickExternal,
  onClose,
  anchorRef,
  withRel,
}) => {
  const [query, setQuery] = React.useState("");
  const [fetchedExternals, setFetchedExternals] = React.useState<ExternalCandidate[]>([]);
  const [selected, setSelected] = React.useState<DepCandidate | null>(null);
  const [relType, setRelType] = React.useState<DepGraphPredType>(
    withRel?.initialType ?? "FS",
  );
  const [lagStr, setLagStr] = React.useState(() => {
    const l = withRel?.initialLagDays ?? 0;
    return Number.isInteger(l) ? String(l) : l.toFixed(2);
  });
  const commitRel = () => {
    if (!withRel) return;
    if (!selected && !withRel.currentId) return;
    const parsed = parseFloat(lagStr);
    withRel.onCommit(selected, relType, Number.isNaN(parsed) ? 0 : parsed);
    onClose();
  };
  // The checked row: an explicit pick, else the pred being edited.
  const checkedId = withRel ? selected?.instanceId ?? withRel.currentId ?? null : null;
  const choose = (c: DepCandidate) => {
    if (withRel) {
      setSelected((prev) => (prev?.instanceId === c.instanceId ? null : c));
    } else {
      onPick?.(c);
    }
  };
  const wrapRef = React.useRef<HTMLDivElement>(null);
  // Callback ref fires when the input mounts (works for both inline and
  // portaled modes) and doesn't re-fire on coords changes during scroll.
  const focusInput = React.useCallback((el: HTMLInputElement | null) => {
    el?.focus();
  }, []);
  const floatingCoords = useFloatingCoords(
    anchorRef ?? { current: null },
    anchorRef != null,
    wrapRef,
  );

  // Floating mode portals outside the anchor's DOM subtree, so a click on the
  // anchor looks "outside" and would race its onClick toggle. Whitelist it.
  useClickOutside(wrapRef, onClose, true, anchorRef);

  const filtered = React.useMemo(() => {
    if (!query.trim()) return candidates.slice(0, 50);
    return candidates.filter((c) => candidateMatches(c, query)).slice(0, 50);
  }, [candidates, query]);

  // Query-driven external search: debounce-fetch matching out-of-section work
  // from the server as the user types (≥2 chars).
  React.useEffect(() => {
    if (!fetchExternals) return;
    const q = query.trim();
    if (q.length < 2) {
      setFetchedExternals([]);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(() => {
      fetchExternals(q)
        .then((res) => !cancelled && setFetchedExternals(res))
        .catch(() => !cancelled && setFetchedExternals([]));
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [query, fetchExternals]);

  const filteredExternals = React.useMemo(() => {
    if (!onPickExternal) return [];
    // Server-driven results are already query-matched; a static list filters
    // locally (legacy callers).
    const source = fetchExternals ? fetchedExternals : externalCandidates;
    if (source.length === 0) return [];
    const q = query.trim().toLowerCase();
    const list = fetchExternals
      ? source
      : source.filter(
          (c) =>
            !q ||
            c.taskCode.toLowerCase().includes(q) ||
            c.taskName.toLowerCase().includes(q) ||
            (c.wbsName || "").toLowerCase().includes(q),
        );
    // Dedupe by external_activity_id — the discovery endpoint can return the
    // same activity on multiple edges.
    const seen = new Set<number>();
    const out: ExternalCandidate[] = [];
    for (const c of list) {
      if (seen.has(c.externalActivityId)) continue;
      seen.add(c.externalActivityId);
      out.push(c);
      if (out.length >= 20) break;
    }
    return out;
  }, [externalCandidates, fetchedExternals, fetchExternals, onPickExternal, query]);

  const inner = (
    <div
      className={`dep-typeahead${floatingCoords ? " dep-typeahead--floating" : ""}${withRel ? " dep-typeahead--rel" : ""}`}
      style={floatingCoords ? { top: floatingCoords.top, left: floatingCoords.left } : undefined}
      ref={wrapRef}
    >
      <input
        ref={focusInput}
        type="text"
        className="dep-typeahead-input"
        placeholder="Search by name, code, or zone..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
          else if (e.key === "Enter" && filtered.length > 0) {
            if (withRel) choose(filtered[0]);
            else onPick?.(filtered[0]);
          }
        }}
      />
      <div className="dep-typeahead-list">
        {filtered.length === 0 && filteredExternals.length === 0 ? (
          <div className="dep-typeahead-empty">No matches</div>
        ) : (
          <>
            {filtered.length > 0 && (
              <div className="dep-typeahead-group-label">In this section</div>
            )}
            {filtered.map((c) => (
              <button
                key={c.instanceId}
                type="button"
                className={
                  "dep-typeahead-item" +
                  (checkedId === c.instanceId ? " dep-typeahead-item--selected" : "")
                }
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(c);
                }}
              >
                {withRel && (
                  <span className="dep-typeahead-check" aria-hidden="true">
                    {checkedId === c.instanceId ? "✓" : ""}
                  </span>
                )}
                <span
                  className="dep-typeahead-color"
                  style={{ background: c.color, color: autoFontColor(c.color) }}
                >
                  {c.workCode}
                </span>
                <span className="dep-typeahead-names">
                  <span className="dep-typeahead-name">{c.displayName}</span>
                  {c.taskName && c.taskName !== c.displayName && (
                    <span className="dep-typeahead-subname">{c.taskName}</span>
                  )}
                </span>
                {c.complete && (
                  <span className="dep-typeahead-complete" title="Completed work">
                    Complete
                  </span>
                )}
                <span className="dep-typeahead-zone">{c.zoneLabel}</span>
                {c.startIso && c.finishIso && (
                  <span className="dep-typeahead-dates">
                    {formatDateMonthDay(c.startIso)} → {formatDateMonthDay(c.finishIso)}
                  </span>
                )}
              </button>
            ))}
            {filteredExternals.length > 0 && (
              <div className="dep-typeahead-group-label">External activities</div>
            )}
            {filteredExternals.map((c) => (
              <button
                key={`ext-${c.externalActivityId}`}
                type="button"
                className="dep-typeahead-item dep-typeahead-item--external"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPickExternal?.(c);
                }}
              >
                <span
                  className={
                    "dep-typeahead-color dep-typeahead-color--external" +
                    (c.workCode ? "" : " dep-typeahead-color--none")
                  }
                >
                  {c.workCode || ""}
                </span>
                <span className="dep-typeahead-names">
                  <span className="dep-typeahead-name">{c.taskName}</span>
                  <span className="dep-typeahead-subname">{c.taskCode}</span>
                </span>
                {c.wbsName && (
                  <span className="dep-typeahead-zone">{c.wbsName}</span>
                )}
                {c.startIso && c.finishIso && (
                  <span className="dep-typeahead-dates">
                    {formatDateMonthDay(c.startIso)} → {formatDateMonthDay(c.finishIso)}
                  </span>
                )}
              </button>
            ))}
          </>
        )}
      </div>
      {withRel && (
        <div className="dep-typeahead-rel">
          <div className="dep-typeahead-rel-row">
            <span className="dep-typeahead-rel-label">Type</span>
            <div className="dep-typeahead-rel-types">
              {REL_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={
                    "dep-typeahead-rel-type" + (t === relType ? " is-active" : "")
                  }
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setRelType(t)}
                >
                  {t}
                </button>
              ))}
            </div>
            <span className="dep-typeahead-rel-label">Lag (days)</span>
            <input
              type="number"
              className="dep-typeahead-rel-lag"
              step="1"
              value={lagStr}
              onChange={(e) => setLagStr(e.target.value)}
            />
            {withRel.onRemove && (
              <button
                type="button"
                className="dep-typeahead-rel-delete"
                title="Remove this tie"
                onClick={() => {
                  withRel.onRemove?.();
                  onClose();
                }}
              >
                Delete
              </button>
            )}
            <button
              type="button"
              className="dep-typeahead-rel-commit"
              disabled={!selected && !withRel.currentId}
              onClick={commitRel}
            >
              {withRel.commitLabel ?? "Set"}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (anchorRef) return floatingCoords ? createPortal(inner, document.body) : null;
  return inner;
};

export default DepChipTypeahead;
